import React, { useRef, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { useT } from '../i18n/LanguageContext';
import RulesTab from '../features/statements/RulesTab';
import RecipientsTab from '../features/statements/RecipientsTab';
import MessagesTab from '../features/statements/MessagesTab';

export { default as RulesTab } from '../features/statements/RulesTab';
export { default as RecipientsTab, AdvisoryStrip } from '../features/statements/RecipientsTab';
export { default as MessagesTab } from '../features/statements/MessagesTab';
export {
  blobErrorText,
  errorText,
  throttleAware,
  unwrap,
} from '../features/statements/responseSafety';
export {
  money,
  easternToday,
  whenLabel,
  stampLabel,
  serverNow,
} from '../features/statements/formatters';
export {
  buildStatementsCsv,
  compareBy,
  csvCell,
  csvFilename,
  RECIPIENT_COLUMNS,
} from '../features/statements/recipientDomain';
export { heldBackLabel, refusalLabel } from '../features/statements/runPresentation';
export {
  createObjectUrlRegistry,
  downloadBlob,
  PDF_PLACEHOLDER_HTML,
  writePdfPlaceholder,
} from '../features/statements/pdfLifecycle';
export { default as LineItems } from '../features/statements/components/LineItems';
export { default as MessageEditor } from '../features/statements/components/MessageEditor';
export { default as RunDetail, RunModeBadge } from '../features/statements/components/RunDetail';
export { default as ScanWaiting } from '../features/statements/components/ScanWaiting';
export { default as Skeleton } from '../features/statements/components/Skeleton';

/**
 * Statements (WQ-260827 Phase 4, redesigned).
 *
 * Three questions an admin arrives with, in the order they arrive:
 *   1. Who gets a statement, and is what it says correct?          -> Recipients (the landing tab)
 *   2. What do this customer's terms say about their credit?       -> Credit limit messages
 *   3. Under what conditions does any of it actually go out?       -> Rules
 *
 * That third question used to have no answer anywhere in the UI. The page showed a ledger and a
 * message editor, and nothing at all about the sending machinery that is the entire point of the
 * feature - so "are statements going out on Tuesday?" was unanswerable without reading Render's
 * environment variables. It sits last because it is the one an admin asks least often, not because
 * it matters least.
 *
 * ADMIN ONLY, and structurally so: '/statements' is deliberately absent from REP_ALLOWED_PATHS, so
 * App.js wraps this route in RequireAdmin automatically. Every endpoint behind it is @Roles('admin')
 * server-side as well - this file is cosmetic, as roles.js says of the whole client policy.
 *
 * NOTHING ON THIS PAGE SENDS ANYTHING. It reads the ledger and saves recipient, practice,
 * pause, and credit-message settings. The send route is POST /admin/statements/run and absent from
 * statementService, so no component here can name it, let alone reach it.
 */

const TABS = ['Recipients', 'Credit limit messages', 'Rules'];

/**
 * Ids that pair each tab with the panel it controls.
 *
 * The buttons said `role="tab"` and `aria-selected` and stopped there - so a screen reader
 * announced "tab, 1 of 3" and then had nothing to move to, because no element on the page claimed
 * to be the panel that tab controlled. Announcing a pattern half-implemented is worse than not
 * announcing it: it promises navigation that does not exist.
 */
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const tabId = (t) => `statements-tab-${slug(t)}`;
const panelId = (t) => `statements-panel-${slug(t)}`;

/**
 * FIVE WARNINGS, ONE LINE.
 *
 * Every one of these is worth keeping - between them they account for the customers who owe money
 * and are NOT on the list, the ones nobody has ruled on, and the ones whose address will bounce.
 * Stacked as full banners they ran to roughly four hundred and fifty pixels on a real company file
 * and pushed the table itself below the fold, so the page opened on its own caveats rather than on
 * the accounts receivable it exists to show.
 *
 * The COUNT AND THE MONEY STAY ON SCREEN. Only the explanation folds away - the numbers are the
 * signal, the prose is the reason, and the reason is what you read once. One panel open at a time,
 * so the strip can never grow back into the wall it replaced.
 */
export default function Statements() {
  const t = useT();
  const [policyRevision, setPolicyRevision] = useState(0);
  const [tab, setTab] = useState(TABS[0]);
  const tabRefs = useRef([]);
  /** Which tabs have ever been opened. Once true, a tab stays mounted - see the note below. */
  const [seen, setSeen] = useState({ [TABS[0]]: true });
  // TABS[0] is Recipients, and it is the landing tab on purpose: "who is getting a statement, and
  // is it right?" is the question this page is opened for.
  const open = (name) => {
    setTab(name);
    setSeen((s) => (s[name] ? s : { ...s, [name]: true }));
  };
  /**
   * Arrow keys move between tabs; Tab moves out of the strip.
   *
   * This is the half of the pattern that makes `aria-selected` mean something. Without it every tab
   * is its own tab stop and a keyboard user walks through all three to reach the table, which is
   * exactly the behaviour the role claims not to have.
   */
  const onTabKey = (e, i) => {
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    open(TABS[next]);
    tabRefs.current[next]?.focus();
  };
  return (
    <div className="p-6">
      <PageHeader
        title="Statements"
        subtitle="Weekly open-item statements: whether they are going out, who receives one, and what they say."
      />
      <div
        className="flex gap-2 mb-5 border-b border-[#e2e8f0]"
        role="tablist"
        aria-label={t('Statements views')}
      >
        {TABS.map((name, i) => (
          <button
            key={name}
            role="tab"
            id={tabId(name)}
            aria-controls={panelId(name)}
            aria-selected={tab === name}
            // One tab stop for the whole strip, which is what the role promises.
            tabIndex={tab === name ? 0 : -1}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            onKeyDown={(e) => onTabKey(e, i)}
            onClick={() => open(name)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === name
                ? 'border-[#0089ff] text-[#0089ff]'
                : 'border-transparent text-[#64748b] hover:text-[#0f172a]'
            }`}
          >
            {t(name)}
          </button>
        ))}
      </div>
      {/* MOUNTED ON FIRST VISIT, THEN KEPT.
          Unmounting on every switch would re-run each tab's load, and the recipients read walks the
          entire QuickBooks company file behind a one-request-per-minute throttle - so flipping away
          and back would 429 and leave the admin staring at an error for a minute.
          But mounting all three EAGERLY was worse in the other direction: /run-plan and /preview
          each walk that same company file, so simply opening the page cost two full passes over
          every customer, invoice, credit memo and payment - against the per-realm Intuit limit that
          live order invoicing shares. Most visits only ever look at the Overview. */}
      {/* `hidden` is display:none, so an inactive panel is out of the accessibility tree and out of
          the tab order entirely - which is what makes keeping it mounted safe. */}
      <div
        role="tabpanel"
        id={panelId(TABS[0])}
        aria-labelledby={tabId(TABS[0])}
        className={tab === TABS[0] ? '' : 'hidden'}
      >
        {seen[TABS[0]] && (
          <RecipientsTab onPolicyChanged={() => setPolicyRevision((value) => value + 1)} />
        )}
      </div>
      <div
        role="tabpanel"
        id={panelId(TABS[1])}
        aria-labelledby={tabId(TABS[1])}
        className={tab === TABS[1] ? '' : 'hidden'}
      >
        {seen[TABS[1]] && <MessagesTab />}
      </div>
      <div
        role="tabpanel"
        id={panelId(TABS[2])}
        aria-labelledby={tabId(TABS[2])}
        className={tab === TABS[2] ? '' : 'hidden'}
      >
        {seen[TABS[2]] && <RulesTab key={policyRevision} />}
      </div>
    </div>
  );
}

// =================================================================================================
// Rules - what has to be true before anything is emailed, when the next run is, and what past runs
// actually did
// =================================================================================================
